(() => {
  const roots = document.querySelectorAll('[data-unisource-all-reviews]');
  if (!roots.length) return;

  const DEFAULT_API_URL = 'https://shopify-inksoft-poc-node.azurewebsites.net/api/poc/reviews';

  function cleanText(value) {
    return String(value || '').replace(/\s+/g, ' ').trim();
  }

  function clampRating(value) {
    const n = Number.parseFloat(value);
    return Number.isFinite(n) ? Math.max(0, Math.min(5, n)) : 5;
  }

  function parseDate(value) {
    if (!value) return null;
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  function formatDate(value) {
    const d = parseDate(value);
    if (!d) return cleanText(value);
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  }

  function escapeHtml(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  function starString(value) {
    const rounded = Math.round(clampRating(value));
    return '★★★★★'.slice(0, rounded) + '☆☆☆☆☆'.slice(0, 5 - rounded);
  }

  function normalizeReview(review) {
    const dateRaw = cleanText(review.dateRaw || review.date || '');
    return {
      id: cleanText(review.id || ''),
      source: cleanText(review.source || 'feedback'),
      sourceType: cleanText(review.sourceType || ''),
      rating: clampRating(review.rating),
      title: cleanText(review.title || 'Customer review'),
      body: cleanText(review.body || ''),
      author: cleanText(review.author || 'Verified customer'),
      dateRaw,
      date: formatDate(dateRaw),
      timestamp: Number(review.timestamp) || parseDate(dateRaw)?.getTime() || 0,
    };
  }

  function reviewKey(review) {
    if (review.id) return `${review.source}:${review.id}`;
    return [
      review.author.toLowerCase(),
      review.dateRaw.toLowerCase(),
      review.body.toLowerCase().slice(0, 180),
    ].join('|');
  }

  function dedupeReviews(reviews) {
    const seen = new Set();
    return reviews.filter((r) => {
      const key = reviewKey(r);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  function cardHtml(review) {
    const hasComment = Boolean(cleanText(review.body));
    const body = hasComment ? `<p>${escapeHtml(review.body)}</p>` : '';
    const cardTitle = hasComment ? (review.title || 'Customer review') : 'Rating only';
    const sourceLabel = review.sourceType
      ? `<span class="unisource-all-reviews__source-type">${escapeHtml(review.sourceType)}</span>`
      : '';

    return `
      <article class="unisource-all-reviews__card">
        <div class="unisource-all-reviews__stars" aria-label="${review.rating} out of 5 stars">${starString(review.rating)}</div>
        <strong>${escapeHtml(cardTitle)}</strong>
        ${body}
        <div class="unisource-all-reviews__meta">
          ${escapeHtml(review.author || 'Verified customer')}
          ${review.date ? `<span class="unisource-all-reviews__date">${escapeHtml(review.date)}</span>` : ''}
          ${sourceLabel}
        </div>
      </article>
    `;
  }

  async function fetchReviews(apiUrl) {
    const response = await fetch(apiUrl, {
      method: 'GET',
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    });

    let payload = null;
    try {
      payload = await response.json();
    } catch (_error) {
      throw new Error(`Reviews service returned HTTP ${response.status}`);
    }

    if (!response.ok || !payload?.ok) {
      throw new Error(payload?.error || `Reviews service returned HTTP ${response.status}`);
    }

    return payload;
  }

  function initRoot(root) {
    if (root.dataset.reviewsInitialized === 'true') return;
    root.dataset.reviewsInitialized = 'true';

    const grid = root.querySelector('[data-all-reviews-grid]');
    const empty = root.querySelector('[data-all-reviews-empty]');
    const summary = root.querySelector('[data-all-reviews-summary]');
    const toolbar = root.querySelector('[data-all-reviews-toolbar]');
    const ratingEl = root.querySelector('[data-all-reviews-rating]');
    const starsEl = root.querySelector('[data-all-reviews-stars]');
    const countEl = root.querySelector('[data-all-reviews-count]');
    const resultCountEl = root.querySelector('[data-all-reviews-result-count]');
    const reviewTypeFilter = root.querySelector('[data-all-reviews-type-filter]');
    const ratingFilter = root.querySelector('[data-all-reviews-rating-filter]');
    const sortSelect = root.querySelector('[data-all-reviews-sort]');
    const loadMoreButton = root.querySelector('[data-all-reviews-load-more]');

    const apiUrl = cleanText(root.dataset.reviewsApiUrl || DEFAULT_API_URL);
    const initialCount = Number.parseInt(root.dataset.initialCount || '12', 10) || 12;
    const loadMoreCount = Number.parseInt(root.dataset.loadMoreCount || '8', 10) || 8;

    let allReviews = [];
    let visibleCount = initialCount;
    let total = 0;
    let averageRating = 0;

    function filteredAndSortedReviews() {
      const selectedType = reviewTypeFilter?.value || 'comments';
      const selectedRating = ratingFilter?.value || 'all';
      const selectedSort = sortSelect?.value || 'newest';
      let reviews = [...allReviews];

      // Default view: only ratings that include written customer comments.
      if (selectedType === 'comments') {
        reviews = reviews.filter((r) => Boolean(cleanText(r.body)));
      }

      if (selectedRating !== 'all') {
        const target = Number.parseInt(selectedRating, 10);
        reviews = reviews.filter((r) => Math.round(r.rating) === target);
      }

      reviews.sort((a, b) => {
        if (selectedSort === 'oldest') return (a.timestamp || 0) - (b.timestamp || 0);
        if (selectedSort === 'highest') return b.rating - a.rating || (b.timestamp || 0) - (a.timestamp || 0);
        if (selectedSort === 'lowest') return a.rating - b.rating || (b.timestamp || 0) - (a.timestamp || 0);
        return (b.timestamp || 0) - (a.timestamp || 0);
      });

      return reviews;
    }

    function render() {
      if (!grid) return;
      const reviews = filteredAndSortedReviews();
      const visible = reviews.slice(0, visibleCount);

      if (!reviews.length) {
        grid.innerHTML = '';
        if (empty) empty.hidden = false;
        if (resultCountEl) resultCountEl.textContent = '0 reviews';
        if (loadMoreButton) loadMoreButton.hidden = true;
        return;
      }

      if (empty) empty.hidden = true;
      grid.innerHTML = visible.map(cardHtml).join('');

      if (resultCountEl) {
        const selectedType = reviewTypeFilter?.value || 'comments';
        const selectedRating = ratingFilter?.value || 'all';
        const label = selectedType === 'comments'
          ? `rating${reviews.length === 1 ? '' : 's'} with comments`
          : `review${reviews.length === 1 ? '' : 's'}`;

        if (selectedRating === 'all') {
          resultCountEl.textContent = visible.length === reviews.length
            ? `${reviews.length} ${label}`
            : `Showing ${visible.length} of ${reviews.length} ${label}`;
        } else {
          resultCountEl.textContent = visible.length === reviews.length
            ? `${reviews.length} matching ${label}`
            : `Showing ${visible.length} of ${reviews.length} matching ${label}`;
        }
      }

      if (loadMoreButton) loadMoreButton.hidden = visible.length >= reviews.length;
    }

    function applySummary() {
      if (summary) summary.hidden = false;
      if (toolbar) toolbar.hidden = false;
      const displayRating = averageRating || 5;
      if (ratingEl) ratingEl.textContent = displayRating.toFixed(1);
      if (starsEl) {
        starsEl.textContent = starString(displayRating);
        starsEl.setAttribute('aria-label', `${displayRating.toFixed(1)} out of 5 stars`);
      }
      if (countEl) countEl.textContent = `Based on ${total} customer reviews`;
    }

    async function load() {
      try {
        const payload = await fetchReviews(apiUrl);
        allReviews = dedupeReviews((payload.reviews || []).map(normalizeReview));
        total = Number(payload.total) || allReviews.length;
        averageRating = Number(payload.averageRating) || 0;
        applySummary();
        render();
      } catch (error) {
        console.error('[Unisource Reviews]', error);
        if (grid) grid.innerHTML = '';
        if (empty) {
          empty.hidden = false;
          const p = empty.querySelector('p');
          if (p) p.textContent = 'Customer reviews could not be loaded. Please refresh the page or try again later.';
        }
        if (loadMoreButton) loadMoreButton.hidden = true;
      }
    }

    reviewTypeFilter?.addEventListener('change', () => {
      visibleCount = initialCount;
      render();
    });

    ratingFilter?.addEventListener('change', () => {
      visibleCount = initialCount;
      render();
    });

    sortSelect?.addEventListener('change', () => {
      visibleCount = initialCount;
      render();
    });

    loadMoreButton?.addEventListener('click', () => {
      visibleCount += loadMoreCount;
      render();
    });

    load();
  }

  roots.forEach(initRoot);
})();